import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { toast } from 'react-toastify';
import { parseNativeOAuthCallbackSearch } from '../utils/nativeOAuthCallbackBounce';

/**
 * Deep links auth Google (retour In-App Browser → callback in-app).
 * Ticket #835 : rintintin://open/auth/google/callback + getLaunchUrl (cold start).
 */
const MobileAuthHandler: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let remove: (() => void) | undefined;

    const applyGoogleCallback = async (rawUrl: string) => {
      const search = parseNativeOAuthCallbackSearch(rawUrl);
      if (search === null) return;
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();
      } catch {
        /* déjà fermé */
      }
      navigate(`/auth/google/callback${search}`, { replace: true });
    };

    const setup = async () => {
      const { App: CapApp } = await import('@capacitor/app');
      try {
        const launch = await CapApp.getLaunchUrl();
        if (launch?.url) {
          await applyGoogleCallback(launch.url);
        }
      } catch {
        /* plugin absent en web */
      }
      const handle = await CapApp.addListener('appUrlOpen', (data) => {
        void applyGoogleCallback(data.url).catch((err) => {
          console.error('[MobileAuthHandler] deep link error:', err);
          toast.error(t('auth.deepLinkOpenFailed'));
        });
      });
      remove = () => {
        void handle.remove();
      };
    };

    void setup();
    return () => remove?.();
  }, [navigate, t]);

  return null;
};

export default MobileAuthHandler;
